import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AutoComplete } from "@/components/AutoComplete/AutoComplete";

import {
	useUsageCompanies,
	useUsageCompany,
} from "../../../redux/feature-usage/feature-usage.selectors";
import { flattenAreas, formatCount, formatDate } from "../utils/featureUsage";
import { MetricTitle } from "./MetricHelp";
import { PeriodFilter } from "./UsageFilters";
import { UsageTimeline } from "./UsageTimeline";
import { FeatureCell, UsageColumn, UsageStatus, UsageSummary, UsageTable } from "./UsageTable";

import type { ProductUsagePeriod, ProductUsageCompanyFeatureRow } from "@signalist/shared-types";

export function CompanyViewTab() {
	const { t } = useTranslation();
	const [companyId, setCompanyId] = useState<number | null>(null);
	const [period, setPeriod] = useState<ProductUsagePeriod>(30);
	const companies = useUsageCompanies();
	const query = useUsageCompany(companyId, period);
	const { data } = query;
	const options =
		companies.data?.companies.map((company) => ({ id: company.companyId, title: company.name })) ??
		[];
	const columns: UsageColumn<ProductUsageCompanyFeatureRow>[] = [
		{
			key: "feature",
			title: <MetricTitle metric="feature" />,
			render: (row) => <FeatureCell feature={row} />,
		},
		{
			key: "dailyEvents",
			title: <MetricTitle metric="dailyEvents" />,
			render: (row) => (
				<UsageTimeline
					days={data?.days ?? []}
					label={`${row.label}: ${t("featureUsage.metrics.dailyEvents")}`}
					values={row.dailyEvents}
				/>
			),
		},
		{
			key: "dailyUsers",
			title: <MetricTitle metric="dailyUsers" />,
			render: (row) => (
				<UsageTimeline
					days={data?.days ?? []}
					label={`${row.label}: ${t("featureUsage.metrics.dailyUsers")}`}
					values={row.dailyDistinctUsers}
				/>
			),
		},
	];

	return (
		<>
			<div className="mb-4 flex flex-wrap items-start gap-4">
				<div className="w-72">
					<AutoComplete
						handleSearch={() => undefined}
						data={companies.isError ? [] : options}
						label={t("featureUsage.company")}
						placeholder={t("featureUsage.pickCompany")}
						selectedItem={options.find((company) => company.id === companyId)?.title ?? ""}
						handleSelect={(company) => setCompanyId(company ? Number(company.id) : null)}
						isLoading={companies.isLoading}
						noResultsText={t("featureUsage.noCompanies")}
						statusText={companies.isError ? t("featureUsage.error") : undefined}
					/>
				</div>
				<PeriodFilter value={period} onChange={setPeriod} />
			</div>
			<UsageStatus {...companies} retry={companies.refetch} />
			{companies.data?.lastRolledUpDate && !companies.isError && (
				<>
					{companyId === null ? (
						<p className="py-8 text-text-meta">
							{t(options.length ? "featureUsage.pickCompany" : "featureUsage.noCompanies")}
						</p>
					) : (
						<UsageStatus {...query} retry={query.refetch} />
					)}
					{companyId !== null &&
						data &&
						!query.isError &&
						!query.isLoading &&
						data.lastRolledUpDate !== null && (
							<>
								<UsageSummary data={data} />
								<h2 className="mb-3 text-sm font-semibold text-text">{data.company.name}</h2>
								<div className="mb-6 grid grid-cols-1 gap-4 rounded-lg border border-border-light p-4 tb:grid-cols-3">
									<div>
										<MetricTitle metric="breadth" />
										<div className="mt-2 text-xl font-semibold">
											{t("featureUsage.breadth", {
												used: formatCount(data.featuresUsed),
												total: formatCount(data.catalogSize),
											})}
										</div>
									</div>
									<div>
										<MetricTitle metric="lastActivity" />
										<div className="mt-2 text-xl font-semibold">
											{data.lastActivityDate
												? formatDate(data.lastActivityDate)
												: t("featureUsage.noHistory")}
										</div>
									</div>
									<div>
										<MetricTitle metric="analytics" />
										<div className="mt-2 text-sm">
											{t(data.company.analyticsEnabled ? "featureUsage.on" : "featureUsage.off")}
										</div>
									</div>
								</div>
								<UsageTable
									rows={flattenAreas(data.areas)}
									columns={columns}
									label={t("featureUsage.tabs.company")}
								/>
								<p className="mt-4 rounded bg-brand-light/5 p-3 text-xs text-text-meta">
									{t("featureUsage.distinctNote")}
								</p>
							</>
						)}
				</>
			)}
		</>
	);
}
